/**
 * Pure helpers for the notification screens (tracker item 35). Provider
 * neutral: nothing here knows any provider's fields; the settings form is
 * driven by the adapter's configSchema.
 */

import * as types from '../../api/types';

export const OUTBOX_STATUS_LABELS: Record<types.OutboxStatus, string> = {
  PENDING: 'Queued',
  SENDING: 'Sending',
  SENT: 'Sent',
  FAILED: 'Failed',
  SKIPPED: 'Not sent',
};

export const OUTBOX_STATUS_CLASSES: Record<types.OutboxStatus, string> = {
  PENDING: 'bg-blue-50 text-blue-800',
  SENDING: 'bg-blue-50 text-blue-800',
  SENT: 'bg-green-50 text-green-800',
  FAILED: 'bg-red-50 text-red-800',
  SKIPPED: 'bg-gray-100 text-gray-700',
};

export const DELIVERY_LABELS: Record<types.DeliveryStatus, string> = {
  DELIVERED: 'Delivered',
  BOUNCED: 'Bounced',
  COMPLAINED: 'Marked as spam',
  FAILED: 'Rejected',
};

export const PROVIDER_STATUS_LABELS: Record<types.ProviderConfigStatus, string> = {
  UNVERIFIED: 'Not verified',
  VERIFIED: 'Verified',
  FAILED: 'Verification failed',
};

export const EVENT_LABELS: Record<string, string> = {
  INVOICE_SENT: 'Invoice',
  STATEMENT_SENT: 'Statement',
  PAYMENT_RECEIPT: 'Payment receipt',
  // Automated dunning reminders (tracker item 36)
  DUNNING_1: 'Overdue reminder 1',
  DUNNING_2: 'Overdue reminder 2',
  DUNNING_3: 'Overdue reminder 3',
  DUNNING_4: 'Overdue reminder 4',
  DUNNING_5: 'Overdue reminder 5',
  DUNNING_6: 'Overdue reminder 6',
  // Day-of customer messages (tracker item 43)
  JOB_ON_MY_WAY: 'On my way',
  JOB_COMPLETED: 'Job complete',
  // Estimates (tracker item 39)
  ESTIMATE_SENT: 'Estimate',
  ESTIMATE_RESPONDED: 'Estimate answered (office)',
  // Setup reminder (tracker item 75)
  SETUP_REMINDER: 'Setup reminder (office)',
  // Customer satisfaction survey (tracker item 69)
  SURVEY_REQUEST: 'Customer survey',
  // Portal rollout campaigns (tracker item 81)
  PORTAL_ROLLOUT_WELCOME: 'Customer portal welcome',
  // Service bundle renewals (tracker item 85)
  ENROLLMENT_RENEWED: 'Service bundle renewed',
  TEST: 'Test message',
};

export function eventLabel(eventKey: string): string {
  return EVENT_LABELS[eventKey] ?? eventKey;
}

/** One-line status of a message for lists: "Sent · Delivered", "Not sent: The customer has no email address". */
export function messageStatusText(message: Pick<types.OutboxMessage, 'status' | 'skipReasonText' | 'lastError' | 'deliveryStatus'>): string {
  const base = OUTBOX_STATUS_LABELS[message.status] ?? message.status;
  if (message.status === 'SKIPPED' && message.skipReasonText) return `${base}: ${message.skipReasonText}`;
  if (message.status === 'FAILED' && message.lastError) return `${base}: ${message.lastError}`;
  if (message.deliveryStatus) return `${base} · ${DELIVERY_LABELS[message.deliveryStatus]}`;
  return base;
}

/** Where a message's source record lives in the app, with a short label; null when the source has no page (or none was recorded). */
export function messageSourceLink(message: Pick<types.OutboxMessage, 'sourceType' | 'sourceId'>): { to: string; label: string } | null {
  if (!message.sourceId) return null;
  const id = encodeURIComponent(message.sourceId);
  switch (message.sourceType) {
    case 'INVOICE': return { to: `/invoices/${id}`, label: 'Invoice' };
    case 'JOB': return { to: `/jobs/${id}`, label: 'Job' };
    case 'ESTIMATE': return { to: `/estimates/${id}`, label: 'Estimate' };
    case 'PAYMENT': return { to: `/payments/${id}`, label: 'Payment' };
    default: return null;
  }
}

/** A fresh idempotency key for one Email dialog (a double-click reuses it; a later resend gets a new one). */
export function newIdempotencyKey(): string {
  return `ui-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Form values for a provider's fields: stored settings; secret fields always start blank. */
export function providerFormValues(
  provider: types.NotificationProviderDescriptor,
  config: types.NotificationProviderConfig | null
): Record<string, string> {
  const sameProvider = config?.provider === provider.id;
  const values: Record<string, string> = {};
  for (const field of provider.configSchema) {
    if (field.secret) values[field.key] = '';
    else values[field.key] = (sameProvider ? config?.settings[field.key] : undefined) ?? (field.type === 'select' ? field.options?.[0]?.value ?? '' : '');
  }
  return values;
}

/**
 * PUT /provider body: settings from the non-secret fields; a secret field is
 * only sent when the user typed a new value (blank = keep the stored one).
 */
export function buildProviderSaveRequest(
  provider: types.NotificationProviderDescriptor,
  values: Record<string, string>
): types.SaveNotificationProviderRequest {
  const settings: Record<string, string> = {};
  const credentials: Record<string, string> = {};
  for (const field of provider.configSchema) {
    const value = (values[field.key] ?? '').trim();
    if (field.secret) {
      if (value) credentials[field.key] = value;
    } else {
      settings[field.key] = value;
    }
  }
  return {
    channel: 'EMAIL',
    provider: provider.id,
    settings,
    ...(Object.keys(credentials).length > 0 ? { credentials } : {}),
  };
}

/** Required fields left blank (a stored secret counts as filled for the same provider). */
export function missingProviderFields(
  provider: types.NotificationProviderDescriptor,
  values: Record<string, string>,
  config: types.NotificationProviderConfig | null
): string[] {
  const hasStoredSecrets = config?.provider === provider.id && config.hasCredentials;
  return provider.configSchema
    .filter((field) => field.required && !(values[field.key] ?? '').trim() && !(field.secret && hasStoredSecrets))
    .map((field) => field.label);
}

/** The placeholder text to insert for a variable. */
export function variableToken(name: string): string {
  return `{{${name}}}`;
}

export const NOTIFICATION_SETTINGS_PATH = '/settings/notifications';

/** The page that edits one message type's template. */
export function notificationTemplatePath(eventKey: string): string {
  return `${NOTIFICATION_SETTINGS_PATH}/messages/${encodeURIComponent(eventKey)}`;
}

/** Headings for the message list on Settings > Notifications, in display order. */
export const MESSAGE_GROUPS: { id: string; title: string; eventKeys: readonly string[] }[] = [
  { id: 'documents', title: 'Customer documents', eventKeys: ['INVOICE_SENT', 'STATEMENT_SENT', 'PAYMENT_RECEIPT'] },
  { id: 'dunning', title: 'Overdue reminders', eventKeys: ['DUNNING_1', 'DUNNING_2', 'DUNNING_3', 'DUNNING_4', 'DUNNING_5', 'DUNNING_6'] },
  { id: 'online-payments', title: 'Online payments', eventKeys: ['AUTOPAY_UPCOMING', 'AUTOPAY_FAILED', 'CARD_EXPIRING', 'ONLINE_PAYMENTS_ALERT'] },
  // Tracker item 43: off by default; turning one on here is the company's toggle
  { id: 'day-of', title: 'Day-of messages', eventKeys: ['JOB_ON_MY_WAY', 'JOB_COMPLETED'] },
  // Tracker item 39: the estimate email (view / approve link) and the office's note when a customer answers
  { id: 'estimates', title: 'Estimates', eventKeys: ['ESTIMATE_SENT', 'ESTIMATE_RESPONDED'] },
  // Tracker item 75: the office reminder while required setup steps are open
  // Tracker item 69: the rating request sent after a completed job (turned on with Settings > Surveys)
  { id: 'surveys', title: 'Customer surveys', eventKeys: ['SURVEY_REQUEST'] },
  // Tracker item 81: the welcome email a portal rollout campaign sends (on by default; only a campaign sends it)
  { id: 'portal-rollout', title: 'Customer portal rollout', eventKeys: ['PORTAL_ROLLOUT_WELCOME'] },
  // Tracker item 85: confirmation to the customer when a renewal of their service bundle is approved (off by default)
  { id: 'service-bundles', title: 'Service bundles', eventKeys: ['ENROLLMENT_RENEWED'] },
  { id: 'setup', title: 'Account setup', eventKeys: ['SETUP_REMINDER'] },
];

/**
 * Templates grouped for the message list: the known groups in order (the
 * server's order inside each), then any event the UI does not know yet under
 * "Other". Empty groups are left out.
 */
export function groupMessageTemplates(
  templates: types.NotificationTemplate[]
): { id: string; title: string; templates: types.NotificationTemplate[] }[] {
  const known = new Set(MESSAGE_GROUPS.flatMap((g) => g.eventKeys));
  const groups = MESSAGE_GROUPS.map((g) => ({
    id: g.id,
    title: g.title,
    templates: templates.filter((t) => g.eventKeys.includes(t.eventKey)),
  }));
  groups.push({ id: 'other', title: 'Other', templates: templates.filter((t) => !known.has(t.eventKey)) });
  return groups.filter((g) => g.templates.length > 0);
}

/** Insert text at the cursor (or replace the selection); returns the new value and cursor position. */
export function insertAtCursor(value: string, text: string, start: number | null, end: number | null): { value: string; cursor: number } {
  const from = start === null ? value.length : Math.max(0, Math.min(start, value.length));
  const to = end === null ? from : Math.max(from, Math.min(end, value.length));
  return { value: value.slice(0, from) + text + value.slice(to), cursor: from + text.length };
}
